// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : react hooks for job system
// =============================================================================
// ---Third-party imports
import now from 'performance-now';
// ---Framework imports
import { Sel } from 'uix/registries';
import { size, get, sum, values, isArray, dict } from 'ldash';
import log from 'uix/logging';
// ---Local imports
import * as jobactions from './actions';
import { useSelector, useActions, useApi, useMemo, useState, useEffect } from 'uix/hooks';
// ---Exports
// keep this aligned with vls/job/utils.ACTIVE_STATES
export const ACTIVE_STATES = new Set([
    'RUNNING',
    'STARTING',
    'STARTING_WAITING',
    'STARTING_EXTERNAL',
    'STOPPING'
]);
export const useJobs = ()=>{
    let jobSpec = useSelector((root)=>root.config.jobSpec);
    let active = useSelector((root)=>root.data.job.active);
    let domains = useSelector(Sel.domains);
    let actions = useActions(jobactions);
    let specLoaded = size(jobSpec) > 0;
    useEffect(()=>{
        if (!specLoaded) actions.loadJobSpec();
    }, []);
    return {
        jobSpec,
        specLoaded,
        active,
        domains,
        ...actions
    };
};
export const useLastJob = (name)=>{
    // only refetch data if number of finished jobs with matching name changes
    let evts = useSelector((root)=>root.data.serverEvents.events.EJobFinished) || [];
    let finishedCount = evts.filter((e)=>e.name == name);
    let { data, error } = useApi('job/last', {
        name
    }, {
        condition: !!name,
        cached: true,
        deps: [
            finishedCount
        ]
    });
    return useMemo(()=>({
            ojid: data?.ojid,
            loading: !data && !error
        }), [
        data,
        error
    ]);
};
export const useJobEvents = (ojid)=>{
    /* we track the counts in jobInfo.events
  and reload the event messages on count changes
  */ let loadJobEvents = useActions('loadJobEvents');
    let evdata = useSelector((root)=>root.data.job.events[ojid]);
    let jobInfo = useSelector((root)=>root.data.job.info[ojid]);
    // also reload events if eventcount changes
    // depend on monotonic increasing
    // XXX: maybe fetch only "new" events
    // or conditionalize on evt_count > eventCount + AT_LEAST_N_NEW
    let [eventCount, setEventCount] = useState(0);
    let evt_count = 0;
    if (jobInfo) {
        let c = sum(values(jobInfo.events || {}));
        // info events are not stored, subtract them
        evt_count = (c - jobInfo.events?.info) ?? 0;
    }
    // wait some time before fetching first events
    let first_load_ts = useMemo(()=>now(), [
        ojid
    ]);
    useEffect(()=>{
        if (ojid) loadJobEvents(ojid);
    }, [
        first_load_ts
    ]);
    useEffect(()=>{
        // ignore initial phase
        let START_MSEC = 3000 // in ms
        ;
        if (now() - first_load_ts < START_MSEC) return;
        // throttle if long duration
        let FACTOR = 30;
        if (evdata) {
            let min_duration = evdata.api_stats.duration * FACTOR;
            if (now() - evdata.api_stats.ts < min_duration) {
                return;
            }
        }
        // no data or refresh needed
        if (ojid && (!evdata || evt_count > eventCount)) {
            loadJobEvents(ojid);
            setEventCount(evt_count);
        }
    }, [
        ojid,
        evt_count,
        jobInfo?.state
    ]);
    let ignore = (evt)=>evt.type == 'initKwds' || evt.type == 'config';
    let parseEvents = (events)=>{
        let byType = {
            warning: [],
            error: []
        };
        let filtered = [];
        events.forEach((evt)=>{
            if (!ignore(evt)) {
                filtered.push(evt);
                if (!byType[evt.type]) {
                    byType[evt.type] = [];
                }
                byType[evt.type].push(evt);
            }
        });
        return {
            byType,
            events: filtered
        };
    };
    return useMemo(()=>({
            ...parseEvents(evdata?.events || []),
            states: evdata?.states || [],
            job: evdata?.meta || {},
            loading: !evdata
        }), [
        evdata
    ]);
};
export const useActiveJobInfo = (ojid)=>{
    let jobInfo = useJobInfo(ojid);
    let job = useSelector((root)=>root.data.job.info[ojid]);
    jobInfo = useMemo(()=>{
        if (!job) {
            return jobInfo;
        }
        return {
            ...jobInfo,
            ...job
        };
    }, [
        jobInfo,
        job
    ]);
    return jobInfo;
};
export const useJobInfo = (ojid)=>{
    /* job info for ojid (non-updating)
   */ let jobinfos = useJobInfos({
        ojid,
        resolveDependencies: false
    });
    let jobInfo = useMemo(()=>{
        if (!jobinfos.loading) {
            return {
                loading: false,
                ...jobinfos.job
            };
        }
        return {
            loading: true
        };
    }, [
        ojid,
        jobinfos
    ]);
    return jobInfo;
};
export const useJobInfos = ({ ojid, resolveDependencies = true })=>{
    /* info for this ojid (on key: job)
    and child jobs (on key: jobs)
  */ let actions = useActions(jobactions);
    let old_info = useSelector((root)=>root.data.job.info[ojid]);
    let { data, error, loading } = useApi('job/info', {
        ojids: ojid,
        details: true,
        resolveDependencies
    }, {
        condition: !!ojid,
        autoreset: false
    });
    useEffect(()=>{
        if (data?.jobs) {
            actions.updateJobInfo(data.jobs);
        }
    }, [
        data
    ]);
    let result = useMemo(()=>{
        if (!loading && data) {
            let job = data.jobs.filter((j)=>j.ojid == ojid)[0];
            return {
                loading: false,
                jobs: data.jobs,
                job
            };
        }
        return {
            loading,
            jobs: [],
            job: old_info ? old_info : null
        };
    }, [
        loading,
        ojid,
        data,
        error,
        old_info
    ]);
    return result;
};
export const useActiveJobRegistration = ({ name, ojid, state })=>{
    let activeJobs = useSelector((root)=>root.data.job.active);
    let setActiveJob = useActions('setActiveJob');
    useEffect(()=>{
        if (ojid && ACTIVE_STATES.has(state)) {
            let registered = activeJobs.filter((j)=>j.ojid == ojid).length > 0;
            if (!registered) {
                setActiveJob({
                    name,
                    ojid,
                    state
                });
            }
        }
    }, [
        ojid,
        state
    ]);
};
export const useJobFilterApi = ({ names, days, max = 100 })=>{
    names = isArray(names) ? names.join(',') : names;
    let condition = !names || names.length > 2;
    let { data, error, loading } = useApi('filter/getJobs', {
        names,
        max,
        days
    }, {
        autoreset: false,
        condition
    });
    let jobs = useMemo(()=>{
        if (condition && data) {
            return data.jobs;
        }
        return [];
    }, [
        data,
        condition
    ]);
    return {
        jobs,
        loading,
        error
    };
};
export const useOnJobFinished = ({ name, onFinishedJobs })=>{
    /* run onFinishedJobs callback if jobs with matching name are finished
   */ let [watching, setWatching] = useState([]);
    let active = useSelector((root)=>root.data.job.active);
    useEffect(()=>{
        let filtered_active = dict(active.filter((j)=>j.name == name).map((j)=>[
                j.ojid,
                j
            ]));
        let finished_jobs = watching.filter((j)=>!filtered_active[j.ojid]);
        if (finished_jobs.length > 0) {
            onFinishedJobs(finished_jobs);
        }
        setWatching(values(filtered_active));
    }, [
        active
    ]);
};
